"use strict";

import Vue from "vue";
import Vuex from "vuex";
import VueResource from "vue-resource";
import Snotify, { SnotifyPosition } from 'vue-snotify'
// Modules
import cart from "./cart";
import departments from "./departments";
import products from "./products";
import stock from "./stock";
import sales from "./sales";
import till from "./till";

Vue.use( Vuex );
Vue.use( VueResource );

const snotifyOptions = {
    toast: {
        position: SnotifyPosition.rightTop
    }
};

Vue.use( Snotify, snotifyOptions );

// Init store
const grotecStore = new Vuex.Store( {
    state: {
        loading: true, //utilised if I need a loading screen for long data loads
        previousRequest: null, // so I can abort if needed
        toast: {}
    },

    getters: {
        loading: state => {
            return state.loading;
        },

        previousRequest: state => {
            return state.previousRequest;
        },

        currentToast: state => {
            return state.toast;
        }
    },

    mutations: {
        loading: state => {
            state.loading = !state.loading;
        },

        previousRequest: ( state, request ) => {
            state.previousRequest = request;
        },

        currentToast: ( state, toast ) => {
            state.toast = toast;
        }
    },

    actions: {
        cancelPreviousRequest: ( {state, commit}, previousRequest) => {
            if (state.previousRequest !== null) {
                state.previousRequest.cancel();
            }
            commit('previousRequest', previousRequest);
        }
    },

    modules: {
        cart: cart,
        departments: departments,
        products: products,
        stock: stock,
        till: till,
        sales: sales
    }
} );

// Set up api
let uriPrefix = '/api/v2';


let tillActions = {
    layout: {
        method: 'GET',
        url: uriPrefix + '/till/layout'
    },
    drawer: {
        method: 'GET',
        url: uriPrefix + '/till/drawer'
    },
    cash: {
        method: 'POST',
        url: uriPrefix + '/till/cash'
    },
    card: {
        method: 'POST',
        url: uriPrefix + '/till/card'
    },
    print: {
        method: 'GET',
        url: uriPrefix + '/till/print{/cartId}'
    },
    telephonePaid: {
        method: 'POST',
        url: uriPrefix + '/till/telephone-paid/{salesOrderId}',
        emulateJSON: true
    },
    loadKit: {
        method: 'GET',
        url: uriPrefix + '/till{/cartId}/load-kit{/productSlug}'
    },
    createReturn: {
        method: 'POST',
        url: uriPrefix + '/till/create-return'
    }
};

let stockOrdersActions = {
    orderType: {
        method: 'GET',
        url: uriPrefix + '/stock-orders/type{/orderType}'
    },
    allTypes: {
        method: 'GET',
        url: uriPrefix + '/stock-orders/all-types'
    },
    send: {
        method: 'POST',
        url: uriPrefix + '/stock-orders/{stockOrderId}/send'
    },
    addStockOrderItem: {
        method: 'POST',
        url: uriPrefix + '/stock-orders{/stockOrderId}/items'
    },
    updateStockOrderItem: {
        method: 'PUT',
        url: uriPrefix + '/stock-orders{/stockOrderId}/items{/itemId}'
    },
    removeStockOrderItem: {
        method: 'DELETE',
        url: uriPrefix + '/stock-orders{/stockOrderId}/items{/itemId}'
    },
    moveStockOrderItem: {
        method: 'POST',
        url: uriPrefix + '/stock-orders{/stockOrderId}/items{/itemId}/move'
    },
    received: {
        method: 'POST',
        url: uriPrefix + '/stock-orders{/stockOrderId}/received'
    },
    receivedBackorderedItem: {
        method: 'POST',
        url: uriPrefix + '/stock-orders{/stockOrderId}/items{/itemId}/receive'
    },
    release: {
        method: 'POST',
        url: uriPrefix + '/stock-orders{/stockOrderId}/release'
    }
    
};

let suppliersActions = {
    getProducts: {
        method: 'GET',
        url: uriPrefix + '/suppliers{/supplierSlug}/products'
    },
    linkProduct: {
        method: 'POST',
        url: uriPrefix + '/suppliers{/suppliersSlug}/products'
    },
    updateLink: {
        method: 'PUT',
        url: uriPrefix + '/suppliers{/suppliersSlug}/products{/productSlug}'
    },
    removeLink: {
        method: 'DELETE',
        url: uriPrefix + '/suppliers{/suppliersSlug}/products{/productSlug}'
    },
    total: {
        method: 'GET',
        url: uriPrefix + '/suppliers{/suppliersSlug}/total/'
    }
};

let boxTypeActions = {};

let customerActions = {
    search: {
        method: 'POST',
        url: uriPrefix + '/customer/search'
    },
    byLastName: {
        method: 'GET',
        url: uriPrefix + '/customer/last-name/{lastName}'
    },
    byPostCode: {
        method: 'GET',
        url: uriPrefix + '/customer/postcode/{postcode}'
    }
};

let salesActions = {
    salesCounts: {
        method: 'GET',
        url: uriPrefix + '/sales/counts'
    },
    filter: {
        method: 'POST',
        url: uriPrefix + '/sales/filter{/page}'
    },
    toggleAction: {
        method: 'PUT',
        url: uriPrefix + '/sales{/salesId}/toggle{/action}'
    },
    printReceipt: {
        method: 'GET',
        url: uriPrefix + '/sales{/salesId}/till-receipt'
    },
    printFullPriceReceipt: {
        method: 'GET',
        url: uriPrefix + '/sales{/salesId}/full-price-till-receipt'
    },
    emailCopyInvoice: {
        method: 'GET',
        url: uriPrefix + '/sales{/salesId}/email-invoice'
    },
    backorders: {
        method: 'GET',
        url: uriPrefix + '/sales/backorders'
    },
    placeBackorder: {
        method: 'POST',
        url: uriPrefix + '/sales/backorders/place'
    },
    loadBackorder: {
        method: 'POST',
        url: uriPrefix + '/sales/backorders{/backordersId}/load'
    },
    addSalesOrderItem: {
        method: 'POST',
        url: uriPrefix + '/sales{/salesId}/items'
    },
    updateSalesOrderItem: {
        method: 'PUT',
        url: uriPrefix + '/sales{/salesId}/items{/itemId}'
    },
    removeSalesOrderItem: {
        method: 'DELETE',
        url: uriPrefix + '/sales{/salesId}/items{/itemId}'
    },
    moveSalesOrderItem: {
        method: 'POST',
        url: uriPrefix + '/sales{/salesId}/items{/itemId}/move'
    },
    updateSalesOrderItemByProduct: {
        method: 'PUT',
        url: uriPrefix + '/sales{/salesId}/products{/productId}'
    },
    removeSalesOrderItemByProduct: {
        method: 'DELETE',
        url: uriPrefix + '/sales{/salesId}/products{/productId}'
    },
    byCustomerLastName: {
        method: 'GET',
        url: uriPrefix + '/sales/last-name{/lastName}'
    },
    clear: {
        method: 'DELETE',
        url: uriPrefix + '/sales{/salesId}/items'
    },
    refund: {
        method: 'POST',
        url: uriPrefix + '/sales{/salesId}/refund'
    },
    release: {
        method: 'GET',
        url: uriPrefix + '/sales{/salesId}/release'
    },
    takeAll: {
        method: 'POST',
        url: uriPrefix + '/sales{/salesId}/take-all'
    }
};

let cartActions = {};

let barcodeActions = {
    product: {
        method: 'GET',
        url: uriPrefix + '/barcodes{/barcode}/product'
    }
};
// Commit api
//grotecStore.commit( 'barcodesResource', Vue.resource( uriPrefix + '/barcodes{/barcode}', {}, barcodeActions ) );
//grotecStore.commit( 'departmentsProductsResource', Vue.resource( uriPrefix + '/departments{/departmentSlug}/products{/productSlug}' ) );
//grotecStore.commit( 'tillResource', Vue.resource( uriPrefix + '/till{/cartId}', {}, tillActions ) );
//grotecStore.commit( 'customersResource', Vue.resource( uriPrefix + '/customer{/customerId}', {}, customerActions ) );
//grotecStore.commit( 'salesResource', Vue.resource( uriPrefix + '/sales{/salesId}', {}, salesActions ) );
//export default grotecStore;



// Commit api
grotecStore.commit( 'barcodesResource', Vue.resource( uriPrefix + '/barcodes{/barcode}', {}, barcodeActions ) );
grotecStore.commit( 'cartResource', Vue.resource( uriPrefix + '/cart{/cartId}', {}, cartActions ) );
grotecStore.commit( 'departmentsProductsResource', Vue.resource( uriPrefix + '/departments{/departmentSlug}/products{/productSlug}' ) );
grotecStore.commit( 'tillResource', Vue.resource( uriPrefix + '/till{/cartId}', {}, tillActions ) );
grotecStore.commit( 'stockOrdersResource', Vue.resource( uriPrefix + '/stock-orders{/stockOrderId}', {}, stockOrdersActions ) );
grotecStore.commit( 'suppliersResource', Vue.resource( uriPrefix + '/suppliers{/supplierSlug}', {}, suppliersActions ) );
grotecStore.commit( 'boxTypesResource', Vue.resource( uriPrefix + '/box-types{/boxTypeSlug}', {}, boxTypeActions ) );
grotecStore.commit( 'customersResource', Vue.resource( uriPrefix + '/customer{/customerId}', {}, customerActions ) );
grotecStore.commit( 'salesResource', Vue.resource( uriPrefix + '/sales{/salesId}', {}, salesActions ) );
export default grotecStore;